import {
  CompRef,
  DocumentServicesObject,
  StudioWidgetComponentData,
  AppDataComponent,
} from '@wix/document-services-types';
import { memoize } from 'lodash';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentTypes } from '@wix/editor-const';
import { ComponentType as CompType } from '@wix/ambassador-app-service-webapp/types';
import {
  buildComponentPreviewState,
  createWidgetPluginComponentDefinition,
  defaultSlotName,
  getContainer_DEPRECATED_BAD_PERFORMANCE,
} from '../utils/pluginUtils';
import { ComponentPreviewState } from '../constants';
import { PluginHostWidgetApi } from '../types';

export interface StudioWidgetComponent {
  type: 'STUDIO_WIDGET';
  name: string;
  componentId: string;
  data: StudioWidgetComponentData['compData']['studioWidgetComponentData'];
}
type AppDataComponentExtended = AppDataComponent | StudioWidgetComponent;

function reportGetWidgetSlotsError(error: any) {
  ErrorReporter.captureException(error, {
    tags: { widgetPlugins: true },
  });
  console.error(error);
}
// NOTE: It's quite heavy, but widget slots are not changed during the session, so we can cache the result
const getWidgetSlotCompRefsCached = memoize(
  function (
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): CompRef[] {
    return getWidgetSlotCompRefs(documentServicesAPI, compRef);
  },
  (documentServicesAPI, compRef) =>
    compRef &&
    `${compRef.id}-${compRef.type}-${documentServicesAPI.components.is.rendered(
      compRef,
    )}`,
);

function getWidgetSlotCompRefsForGfpp(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef[] {
  return getWidgetSlotCompRefsCached(documentServicesAPI, compRef);
}

function isValidStudioVersion(
  component: StudioWidgetComponent | undefined,
): boolean {
  const minValidStudioVersion = 2;

  if (!component) {
    return false;
  }

  const blocksVersion = component.data?.blocksVersion ?? '';
  const [major] = blocksVersion?.split('.');
  return parseInt(major, 10) >= minValidStudioVersion;
}

function isStudioComponent(
  documentServicesAPI: DocumentServicesObject,
  refComponentPointer: CompRef,
): boolean {
  const { appDefinitionId, widgetId, type } =
    documentServicesAPI.components.data.get(refComponentPointer);
  if (type !== 'WidgetRef') {
    return false;
  }

  const appData =
    documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);
  if (!appData) {
    return false;
  }

  /*
    for some reason runtime type `AppDataComponent` that comes from csm
    missed `STUDIO_WIDGET` component type with expected `blocksVersion` property
    so we manually extend `AppDataComponent` type
  */
  const appDataComponents =
    appData.components as unknown as AppDataComponentExtended[];

  const studioWidgetComponent = appDataComponents.find(
    (component): component is StudioWidgetComponent =>
      component.componentId === widgetId &&
      component.type === CompType.STUDIO_WIDGET,
  );

  return isValidStudioVersion(studioWidgetComponent);
}

function getSlotsFromDm(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): Omit<WidgetSlot, 'placement'>[] {
  if (isStudioComponent(documentServicesAPI, widgetCompRef)) {
    try {
      return documentServicesAPI.components.slots.getWidgetSlots(
        widgetCompRef,
      ) as Omit<WidgetSlot, 'placement'>[]; // incopatibility between inner and DM types, need to fix;
    } catch (e) {
      reportGetWidgetSlotsError(e);
      return [];
    }
  }
  return [];
}

function getWidgetSlotCompRefs(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): CompRef[] {
  return getSlotsFromDm(documentServicesAPI, widgetCompRef).map(
    ({ compRef }) => compRef,
  );
}

function getWidgetHostRef(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef {
  return documentServicesAPI.components.refComponents.isReferredComponent(
    compRef,
  )
    ? documentServicesAPI.components.refComponents.getRootRefHostCompPointer(
        compRef,
      )
    : compRef;
}

function getPlacement(
  documentServicesAPI: DocumentServicesObject,
  slot: Omit<WidgetSlot, 'placement'>,
) {
  const hostRef = getWidgetHostRef(documentServicesAPI, slot.compRef);
  const { widgetId, appDefinitionId } =
    documentServicesAPI.components.data.get(hostRef);
  return {
    appDefinitionId,
    widgetId,
    slotId: slot.role,
  };
}

function isWidgetInWidget(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): boolean {
  const widgetCompRef =
    documentServicesAPI.components.getType(compRef) === ComponentTypes.AppWidget
      ? documentServicesAPI.components.refComponents.getRefHostCompPointer(
          compRef,
        )
      : compRef;

  if (!widgetCompRef) {
    return false;
  }

  return (
    documentServicesAPI.components.refComponents.isReferredComponent(
      widgetCompRef,
    ) &&
    documentServicesAPI.components.getType(widgetCompRef) ===
      ComponentTypes.RefComponent
  );
}

export const blocksHostAPI: PluginHostWidgetApi = {
  getWidgetHostRef,
  getSlotPlaceholderDisplayName(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): string | undefined {
    const stageData =
      documentServicesAPI.platform.controllers.getConnectedComponentStageData(
        compRef,
      );
    return stageData?.displayName;
  },
  getWidgetSlot(
    documentServicesAPI: DocumentServicesObject,
    slotCompRef: CompRef,
  ): WidgetSlot {
    const hostRef = getWidgetHostRef(documentServicesAPI, slotCompRef);
    const slots = getSlotsFromDm(documentServicesAPI, hostRef);
    const slot = slots.find((s) => s.compRef.id === slotCompRef.id);
    if (!slot) {
      throw new Error(`Slot not found for ${slotCompRef.id}`);
    }
    return {
      ...slot,
      placement: getPlacement(documentServicesAPI, slot),
    };
  },
  getWidgetSlots(
    documentServicesAPI: DocumentServicesObject,
    widgetCompRef: CompRef,
  ): WidgetSlot[] {
    const slots = getSlotsFromDm(documentServicesAPI, widgetCompRef);
    return slots.map((slot) => ({
      ...slot,
      placement: getPlacement(documentServicesAPI, slot),
    }));
  },
  async populateWidgetPluginToSlot(
    documentServicesAPI: DocumentServicesObject,
    slotComponentRef: CompRef,
    widgetPointer: WidgetPointer,
  ): Promise<CompRef> {
    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
      widgetPointer.appDefinitionId,
    );

    const widgetComponent = await createWidgetPluginComponentDefinition(
      documentServicesAPI,
      widgetPointer,
      appData.applicationId!,
    );

    const compRef: CompRef = documentServicesAPI.components.slots.populate(
      slotComponentRef,
      defaultSlotName,
      widgetComponent,
    );
    return compRef;
  },
  uninstallWidgetPlugin(
    documentServicesAPI: DocumentServicesObject,
    slotComponentRef: CompRef,
  ) {
    documentServicesAPI.components.slots.remove(
      slotComponentRef,
      defaultSlotName,
    );
  },
  getWidgetPointer(
    documentServicesAPI: DocumentServicesObject,
    slotPlaceholderCompRef: CompRef,
  ): WidgetPointer | undefined {
    const contentRef = documentServicesAPI.components.slots.getSlotsData(
      slotPlaceholderCompRef,
    )[defaultSlotName];

    if (!contentRef) {
      return undefined;
    }

    const { appDefinitionId, widgetId } =
      documentServicesAPI.components.data.get(contentRef);

    return { appDefinitionId, widgetId };
  },
  getSlotPlaceholderRefByContentRef(
    documentServicesAPI: DocumentServicesObject,
    widgetPluginCompRef: CompRef,
  ): CompRef | undefined {
    return (
      getContainer_DEPRECATED_BAD_PERFORMANCE(
        documentServicesAPI,
        widgetPluginCompRef,
      ) || undefined
    );
  },
  getSlotPosition(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): WidgetSlotPosition | null {
    const compLayout =
      documentServicesAPI.components.layout.getRelativeToScreenConsideringScroll(
        compRef,
      );
    return {
      y: compLayout.y,
      height: compLayout.height,
    };
  },
  getSlotContentRef(
    documentServicesAPI: DocumentServicesObject,
    slotComponentRef: CompRef,
  ): CompRef | undefined {
    return documentServicesAPI.components.slots.getSlotsData(slotComponentRef)[
      defaultSlotName
    ];
  },
  getCompRefForSlotSelection(
    _: DocumentServicesObject,
    compRef: CompRef,
  ): CompRef {
    return compRef;
  },
  hasWidgetSlot(
    documentServicesAPI: DocumentServicesObject,
    widgetCompRef: CompRef,
  ): boolean {
    if (isWidgetInWidget(documentServicesAPI, widgetCompRef)) {
      return false;
    }

    const widgetHostRef = getWidgetHostRef(documentServicesAPI, widgetCompRef);

    return (
      getWidgetSlotCompRefsForGfpp(documentServicesAPI, widgetHostRef).length >
      0
    );
  },
  async highlightSlot(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ) {
    documentServicesAPI.documentMode.setComponentPreviewState(
      compRef.id,
      buildComponentPreviewState(
        defaultSlotName,
        ComponentPreviewState.HIGHLIGHTED,
      ),
    );
    await documentServicesAPI.waitForChangesAppliedAsync();
  },
  unhighlightSlot(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ) {
    documentServicesAPI.documentMode.setComponentPreviewState(
      compRef.id,
      undefined,
    );
  },
  isPluginComponent(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): boolean {
    const parentRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
      documentServicesAPI,
      compRef,
    );
    if (!parentRef) {
      return false;
    }

    return (
      documentServicesAPI.components.getType(parentRef) ===
      ComponentTypes.SlotsPlaceholder
    );
  },
  isWidgetSlotPopulated(
    documentServicesAPI: DocumentServicesObject,
    slotComponentRef: CompRef,
  ) {
    return !!documentServicesAPI.components.slots.getSlotsData(
      slotComponentRef,
    )?.[defaultSlotName];
  },
};
